import React from "react"
import moment from "moment"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { usePersonnelDetailsContext } from "../PersonnelDetails"
import { personnelGoogleCreateAccountRequest, personnelGoogleCreateGroupsRequest, personnelGoogleCreateSharedDriveRequest, personnelGoogleDiagnosticRequest } from "../../core/_requests"
import { IPersonnelGoogleDiagnosticGroup, IPersonnelGoogleDiagnosticModel } from "../../core/_models"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"

const escapeHtml = (s: string) =>
     String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string))

// Badge Oui / Non (vert / rouge), avec libellés personnalisables et inversion de couleur si besoin.
const YesNo = ({ value, yes = "Oui", no = "Non", invert = false }: { value?: boolean; yes?: string; no?: string; invert?: boolean }) => {
     const good = invert ? !value : value
     return <span className={`badge badge-light-${good ? "success" : "danger"}`}>{value ? yes : no}</span>
}

const Row = ({ label, value }: { label: string; value?: React.ReactNode }) => (
     <div className="d-flex justify-content-between align-items-center py-2 border-bottom border-gray-100">
          <span className="text-muted">{label}</span>
          <span className="fw-bold text-end ms-3">{value === undefined || value === null || value === "" ? <span className="text-muted fw-normal">—</span> : value}</span>
     </div>
)

const fmtDate = (v?: string) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : undefined)

const GROUP_LABELS: Record<string, string> = {
     TYPE_TEAM: "Groupe équipe (team)",
     TYPE_HIERARCHY: "Groupe hiérarchie (responsables)",
}

const GroupCard = ({ group }: { group: IPersonnelGoogleDiagnosticGroup }) => {
     const [open, setOpen] = React.useState(false)
     return (
          <div className="card mb-4">
               <div className="card-header align-items-center">
                    <h4 className="card-title mb-0">{GROUP_LABELS[group.type] ?? group.type}</h4>
                    <div className="card-toolbar">
                         <YesNo value={group.existsInGoogle} yes="Présent dans Google" no="Absent de Google" />
                    </div>
               </div>
               <div className="card-body">
                    <Row label="Email enregistré (base)" value={group.emailOrId} />
                    <Row label="Existe dans Google" value={<YesNo value={group.existsInGoogle} />} />
                    <Row label="Collaborateur membre du groupe" value={<YesNo value={group.collaboratorIsMember} />} />
                    <Row label="Nombre de membres" value={group.membersCount} />
                    {group.membersCount > 0 && (
                         <div className="mt-3">
                              <span className="text-primary fw-bold cursor-pointer" onClick={() => setOpen(o => !o)}>
                                   {open ? "Masquer" : "Afficher"} les membres <i className={`fas fa-chevron-${open ? "up" : "down"} ms-1 fs-8`} />
                              </span>
                              {open && (
                                   <div className="mt-2">
                                        {group.members.map((m, i) => (
                                             <div key={i} className="d-flex justify-content-between align-items-center py-1 border-bottom border-gray-100">
                                                  <span className="text-dark">{m.email}</span>
                                                  <span className="badge badge-light">{m.role}</span>
                                             </div>
                                        ))}
                                   </div>
                              )}
                         </div>
                    )}

                    {/* Arbre ascendant : groupes hiérarchie parents (ce groupe est membre de …, qui est membre de …) */}
                    {group.type === "TYPE_HIERARCHY" && (
                         <div className="mt-4">
                              <div className="fw-bold text-gray-700 mb-2">Arbre hiérarchique (groupes parents)</div>
                              {(group.ancestorGroups ?? []).length === 0 ? (
                                   <div className="text-muted fs-7">Ce groupe n'est membre d'aucun autre groupe (sommet de la hiérarchie).</div>
                              ) : (
                                   <div className="d-flex flex-column">
                                        {(group.ancestorGroups ?? []).map((a, i) => (
                                             <div key={i} className="d-flex align-items-center py-1" style={{ paddingLeft: `${(a.depth - 1) * 1.5}rem` }}>
                                                  <i className="fas fa-level-up-alt fa-rotate-90 text-muted me-2 fs-8" />
                                                  <span className="fw-bold text-dark me-2">{a.name || a.email}</span>
                                                  {a.name && <span className="text-muted fs-8">{a.email}</span>}
                                             </div>
                                        ))}
                                   </div>
                              )}
                         </div>
                    )}
               </div>
          </div>
     )
}

const GoogleDiagnostic = () => {
     const context = usePersonnelDetailsContext()
     const personnelId = context.detailsQuery.data?.personnel.id as number

     const [busy, setBusy] = React.useState(false)

     const query = useQuery<IPersonnelGoogleDiagnosticModel, AxiosError>(
          ["REACT_QUERY_KEY_GET_PERSONNEL_GOOGLE_DIAGNOSTIC", personnelId],
          async () => (await personnelGoogleDiagnosticRequest(personnelId)).data,
          { enabled: !!personnelId, refetchOnWindowFocus: false, retry: false }
     )

     // Crée le compte Google, puis affiche le mot de passe temporaire à communiquer, et rafraîchit le diagnostic.
     async function handleCreateAccount() {
          const confirm = await Swal.fire({
               icon: "question",
               title: "Créer le compte Google ?",
               html: "Un compte Google Workspace sera créé pour ce collaborateur, avec un mot de passe temporaire. Continuer ?",
               showCancelButton: true,
               confirmButtonText: "Oui, créer le compte",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#009ef7",
          })
          if (!confirm.isConfirmed) return
          setBusy(true)
          try {
               const r = await personnelGoogleCreateAccountRequest(personnelId)
               await Swal.fire({
                    icon: "success",
                    title: "Compte Google créé",
                    html: `<div class="text-start fs-6">
                              <div class="mb-2"><b>Compte :</b> ${escapeHtml(r.data.email)}</div>
                              <div class="mb-2"><b>Mot de passe temporaire :</b> <code>${escapeHtml(r.data.tempPassword)}</code></div>
                              <div class="text-muted">À communiquer au collaborateur — il devra le changer à la première connexion.</div>
                         </div>`,
                    confirmButtonText: "OK",
                    width: 560,
               })
               query.refetch()
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de la création du compte Google.", { autoClose: false })
          } finally {
               setBusy(false)
          }
     }

     // Crée les groupes GWS manquants (équipe / hiérarchie) et rafraîchit le diagnostic.
     async function handleCreateGroups() {
          const confirm = await Swal.fire({
               icon: "question",
               title: "Créer les groupes GWS manquants ?",
               html: "Les groupes équipe et hiérarchie absents de Google seront créés et le collaborateur y sera ajouté. Continuer ?",
               showCancelButton: true,
               confirmButtonText: "Oui, créer les groupes",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#009ef7",
          })
          if (!confirm.isConfirmed) return
          setBusy(true)
          try {
               const r = await personnelGoogleCreateGroupsRequest(personnelId)
               const created = r.data.created ?? []
               const warns = r.data.warnings ?? []
               if (created.length === 0 && warns.length === 0) {
                    toast.info("Aucun groupe à créer (déjà présents).")
               } else {
                    const rows = created.map(c => `<li>${escapeHtml(c.email)}</li>`).join("")
                    const warnHtml = warns.length
                         ? `<div class="alert alert-warning text-start mt-3 mb-0"><b>Avertissements</b><ul class="mb-0 mt-1 ps-3">${warns.map(w => `<li>${escapeHtml(w)}</li>`).join("")}</ul></div>`
                         : ""
                    await Swal.fire({
                         icon: created.length ? "success" : "warning",
                         title: created.length ? "Groupes créés" : "Aucun groupe créé",
                         html: `<div class="text-start fs-6">${created.length ? `<div class="mb-2"><b>Créés :</b></div><ul class="ps-4">${rows}</ul>` : ""}${warnHtml}</div>`,
                         confirmButtonText: "OK",
                         width: 560,
                    })
               }
               query.refetch()
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de la création des groupes.", { autoClose: false })
          } finally {
               setBusy(false)
          }
     }

     // Crée un Drive partagé « Prénom NOM » (gestionnaire de contenu = groupe hiérarchie) et rafraîchit.
     async function handleCreateSharedDrive() {
          const confirm = await Swal.fire({
               icon: "question",
               title: "Créer un Drive partagé ?",
               html: "Un Drive partagé « Prénom NOM » sera créé (gestionnaire : agora@cts-corporate.com ; gestionnaire de contenu : le groupe hiérarchie). Continuer ?",
               showCancelButton: true,
               confirmButtonText: "Oui, créer le Drive",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#009ef7",
          })
          if (!confirm.isConfirmed) return
          setBusy(true)
          try {
               const r = await personnelGoogleCreateSharedDriveRequest(personnelId)
               await Swal.fire({
                    icon: "success",
                    title: "Drive partagé créé",
                    html: `<div class="text-start fs-6">
                              <div class="mb-2"><b>Nom :</b> ${escapeHtml(r.data.name)}</div>
                              <div class="mb-2"><b>Gestionnaire de contenu :</b> ${escapeHtml(r.data.sharedWith)}</div>
                         </div>`,
                    confirmButtonText: "OK",
                    width: 560,
               })
               query.refetch()
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de la création du Drive partagé.", { autoClose: false })
          } finally {
               setBusy(false)
          }
     }

     if (query.isFetching) {
          return (
               <div className="d-flex flex-column align-items-center justify-content-center py-20">
                    <MySimpleSpinner size="xl" />
                    <span className="text-muted mt-4">Interrogation de Google Workspace…</span>
               </div>
          )
     }

     if (query.isError || !query.data) {
          return (
               <MyAlert type="danger" isDismissible={false}>
                    <div className="d-flex align-items-center justify-content-between w-100">
                         <span>{query.error?.response?.data ? (query.error.response.data as any)?.detail ?? "Impossible de charger le diagnostic Google." : "Impossible de charger le diagnostic Google."}</span>
                         <button className="btn btn-sm btn-danger ms-3" onClick={() => query.refetch()}>
                              Réessayer
                         </button>
                    </div>
               </MyAlert>
          )
     }

     const { account, groups, sharedDrives, expectedSharedDriveName, driveLookupEmails, warnings } = query.data

     return (
          <div>
               <div className="d-flex justify-content-between align-items-center mb-5">
                    <h3 className="mb-0">
                         <i className="fab fa-google text-primary me-2" /> Diagnostic Google Workspace
                    </h3>
                    <button className="btn btn-sm btn-light-primary" onClick={() => query.refetch()} disabled={query.isFetching}>
                         <i className="fas fa-arrows-rotate me-1" /> Actualiser
                    </button>
               </div>

               {warnings.length > 0 && (
                    <MyAlert type="warning" classNames="mb-5" isDismissible={false}>
                         <ul className="mb-0 ps-3">
                              {warnings.map((w, i) => (
                                   <li key={i}>{w}</li>
                              ))}
                         </ul>
                    </MyAlert>
               )}

               {/* Compte Google */}
               <div className="card mb-4">
                    <div className="card-header align-items-center">
                         <h4 className="card-title mb-0">Compte Google</h4>
                         <div className="card-toolbar">
                              <YesNo value={account.exists} yes="Compte trouvé" no="Aucun compte" />
                         </div>
                    </div>
                    <div className="card-body">
                         {account.exists ? (
                              <>
                                   <Row label="Adresse principale" value={account.primaryEmail} />
                                   <Row label="Nom complet" value={account.fullName} />
                                   <Row label="Compte suspendu" value={<YesNo value={account.suspended} yes="Oui" no="Non" invert />} />
                                   <Row label="Compte archivé" value={<YesNo value={account.archived} yes="Oui" no="Non" invert />} />
                                   <Row label="Unité organisationnelle" value={account.orgUnitPath} />
                                   <Row label="Administrateur" value={<YesNo value={account.isAdmin} />} />
                                   {account.isDelegatedAdmin && <Row label="Administrateur délégué" value={<YesNo value={account.isDelegatedAdmin} />} />}
                                   <Row
                                        label="Validation en 2 étapes (2SV)"
                                        value={<YesNo value={account.enrolledIn2Sv} yes="Activée" no="Non activée" />}
                                   />
                                   {account.enforcedIn2Sv && <Row label="2SV imposée" value={<YesNo value={account.enforcedIn2Sv} />} />}
                                   <Row label="Créé le" value={fmtDate(account.creationTime)} />
                                   <Row label="Dernière connexion" value={account.lastLoginTime && moment(account.lastLoginTime).year() > 1970 ? fmtDate(account.lastLoginTime) : "Jamais connecté"} />
                                   <Row label="Email de récupération" value={account.recoveryEmail} />
                                   <Row label="Téléphone de récupération" value={account.recoveryPhone} />
                                   <Row label="Alias" value={account.aliases && account.aliases.length > 0 ? account.aliases.join(", ") : undefined} />
                              </>
                         ) : (
                              <>
                                   <MyAlert type="warning" isDismissible={false}>
                                        <>
                                             Aucun compte Google n'est associé à l'adresse <strong>{account.checkedEmail || "(email professionnel non renseigné)"}</strong>.
                                        </>
                                   </MyAlert>
                                   <div className="text-end mt-3">
                                        <button className="btn btn-sm btn-primary" onClick={handleCreateAccount} disabled={busy || !account.checkedEmail}>
                                             {busy ? <MySimpleSpinner size="sm" /> : <><i className="fab fa-google me-1" /> Créer le compte Google</>}
                                        </button>
                                   </div>
                              </>
                         )}
                    </div>
               </div>

               {/* Groupes GWS */}
               <div className="d-flex justify-content-between align-items-center mt-6 mb-3">
                    <h4 className="mb-0">Groupes GWS (base ↔ Google)</h4>
                    {account.exists && (groups.length === 0 || groups.some(g => !g.existsInGoogle)) && (
                         <button className="btn btn-sm btn-light-primary" onClick={handleCreateGroups} disabled={busy}>
                              {busy ? <MySimpleSpinner size="sm" /> : <><i className="fas fa-plus me-1" /> Créer les groupes manquants</>}
                         </button>
                    )}
               </div>
               {!account.exists && (
                    <MyAlert type="info" classNames="mb-3" isDismissible={false}>
                         <>Créez d'abord le compte Google : les groupes (équipe / hiérarchie) ne peuvent pas être créés sans compte.</>
                    </MyAlert>
               )}
               {groups.length === 0 ? (
                    <MyAlert type="info" isDismissible={false}>
                         <>Aucun groupe GWS n'est enregistré en base pour ce collaborateur.</>
                    </MyAlert>
               ) : (
                    groups.map((g, i) => <GroupCard key={i} group={g} />)
               )}

               {/* Drive(s) partagé(s) */}
               <h4 className="mt-6 mb-3">Drive(s) partagé(s)</h4>
               <div className="card">
                    <div className="card-body">
                         <div className="text-muted mb-3">
                              Recherche par nom <span className="fw-bold text-dark">« {expectedSharedDriveName || "—"} »</span> et par partage avec :
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                   {driveLookupEmails.length > 0 ? (
                                        driveLookupEmails.map((e, i) => (
                                             <span key={i} className="badge badge-light-primary">
                                                  {e}
                                             </span>
                                        ))
                                   ) : (
                                        <span className="text-muted">Aucune adresse à rechercher.</span>
                                   )}
                              </div>
                         </div>
                         {sharedDrives.length === 0 ? (
                              <>
                                   <MyAlert type="info" isDismissible={false}>
                                        <>Aucun Drive partagé rattaché à ce collaborateur (ou API Drive indisponible sur cet environnement).</>
                                   </MyAlert>
                                   {groups.some(g => g.type === "TYPE_HIERARCHY" && g.existsInGoogle) ? (
                                        <div className="text-end mt-3">
                                             <button className="btn btn-sm btn-primary" onClick={handleCreateSharedDrive} disabled={busy}>
                                                  {busy ? <MySimpleSpinner size="sm" /> : <><i className="fas fa-folder-plus me-1" /> Créer un Drive partagé</>}
                                             </button>
                                        </div>
                                   ) : (
                                        <div className="text-muted fs-7 mt-2">
                                             Le groupe hiérarchie (gestionnaire de contenu) doit exister dans Google avant de créer le Drive partagé.
                                        </div>
                                   )}
                              </>
                         ) : (
                              sharedDrives.map((d, i) => (
                                   <div key={i} className="d-flex justify-content-between align-items-start py-3 border-bottom border-gray-100">
                                        <div className="me-3">
                                             <div className="fw-bold text-dark">{d.name}</div>
                                             <div className="text-muted fs-8 mb-2">
                                                  ID : {d.id} {d.createdTime && `· créé le ${fmtDate(d.createdTime)}`}
                                             </div>
                                             <div className="d-flex flex-wrap gap-1">
                                                  {d.matchedBy.map((m, j) => (
                                                       <span key={j} className="badge badge-light-success">
                                                            {m === "name" ? "nom" : m}
                                                       </span>
                                                  ))}
                                             </div>
                                             {d.sharedWith.length > 0 && (
                                                  <div className="text-muted fs-8 mt-2">Partagé avec : {d.sharedWith.join(", ")}</div>
                                             )}
                                        </div>
                                        {d.id && (
                                             <a className="btn btn-sm btn-light-primary flex-shrink-0" href={`https://drive.google.com/drive/folders/${d.id}`} target="_blank" rel="noreferrer">
                                                  Ouvrir <i className="fas fa-up-right-from-square ms-1" />
                                             </a>
                                        )}
                                   </div>
                              ))
                         )}
                    </div>
               </div>
          </div>
     )
}

export default GoogleDiagnostic
